import {
  Alert,
  AlertTitle,
  Autocomplete,
  type AutocompleteInputChangeReason,
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  Paper,
  type PaperProps,
  TextField,
} from "@mui/material";
import { useAppConfig } from "config/app-config";
import { usePaginatedAutocompleteOptions } from "hooks/autocomplete-data";
import { useLatch } from "hooks/latch";
import { useDebounce } from "hooks/utils/debounce";
import type React from "react";
import type { ReactNode } from "react";
import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 100;
const SCROLL_LOAD_THRESHOLD_PX = 50;

const PaperFooterContext = createContext<ReactNode>(null);

// Footer sits outside the listbox so appending a page never changes the options MUI uses to reset scroll.
const PaperWithFooter = forwardRef<HTMLDivElement, PaperProps>(
  function PaperWithFooter({ children, ...other }, ref) {
    const footer = useContext(PaperFooterContext);
    return (
      <Paper {...other} ref={ref}>
        {children}
        {footer && (
          // Without this, mousedown on the footer blurs the input and closes the popup before click fires.
          <Box onMouseDown={(event) => event.preventDefault()}>{footer}</Box>
        )}
      </Paper>
    );
  },
);

export interface OemPaginatedAutocompleteProps<T> {
  entityType: "oemColorLibrary" | "oemColorCodeImagio";
  /** Extra query params of the list endpoint. Keep the object stable between renders. */
  params?: Record<string, string>;
  label: string;
  /** Keep the object stable between renders; a new one makes MUI reset the typed text. */
  value: T | null;
  onChange: (option: T | null) => void;
  getOptionLabel: (option: T) => string;
  renderOptionContent: (option: T) => ReactNode;
  isOptionEqualToValue: (option: T, value: T) => boolean;
  /** Shown in front of the selected value inside the input. */
  renderValueAdornment?: (value: T) => ReactNode;
  /** Text of the empty list: for a typed query, or for an unfiltered list. */
  noResultsText: (query: string | undefined) => string;
  disabled?: boolean;
  testId?: string;
}

export function OemPaginatedAutocomplete<T>({
  entityType,
  params,
  label,
  value,
  onChange,
  getOptionLabel,
  renderOptionContent,
  isOptionEqualToValue,
  renderValueAdornment,
  noResultsText,
  disabled,
  testId,
}: OemPaginatedAutocompleteProps<T>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wasOpen = useLatch(open);
  const [query, setQuery] = useState<string>();
  const [appConfig] = useAppConfig();
  const debouncedQuery = useDebounce(
    query,
    appConfig.ux.general.inputDebounceTimeMs,
  );

  const { options, error, isLoading, hasMore, loadMore, retry } =
    usePaginatedAutocompleteOptions<T>({
      entityType,
      query: debouncedQuery,
      pageSize: PAGE_SIZE,
      shouldLoad: wasOpen,
      params,
    });
  const hasOptions = options.length > 0;

  let paperFooter: ReactNode = null;
  if (isLoading && hasOptions) {
    paperFooter = (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          py: "var(--spacing8, 8px)",
        }}
      >
        <CircularProgress size={20} />
      </Box>
    );
  } else if (error && hasOptions) {
    paperFooter = (
      <Alert
        severity="error"
        action={
          <Button size="small" color="inherit" onClick={retry}>
            {t("filter_retry")}
          </Button>
        }
      >
        <AlertTitle>{t("error_unexpected_title")}</AlertTitle>
        {error.message}
      </Alert>
    );
  }

  // MUI's effect resets the open listbox to the top on every re-render; this one runs after it and restores the position.
  const lastScrollRef = useRef<{ node: HTMLElement; scrollTop: number }>();
  const previousOptionsRef = useRef(options);
  useEffect(() => {
    const previous = previousOptionsRef.current;
    previousOptionsRef.current = options;
    const isSameOrAppended =
      previous === options ||
      (options.length >= previous.length &&
        previous.every((option, index) => option === options[index]));
    const lastScroll = lastScrollRef.current;
    if (!isSameOrAppended) {
      lastScrollRef.current = undefined;
    } else if (lastScroll && lastScroll.node.scrollTop === 0) {
      lastScroll.node.scrollTop = lastScroll.scrollTop;
    }
  });

  const onListboxScroll = (event: React.UIEvent<HTMLElement>) => {
    const { currentTarget: node } = event;
    const { scrollTop, clientHeight, scrollHeight } = node;
    lastScrollRef.current = { node, scrollTop };
    const isNearBottom =
      scrollTop + clientHeight >= scrollHeight - SCROLL_LOAD_THRESHOLD_PX;
    if (hasMore && isNearBottom) {
      loadMore();
    }
  };

  // Only typed text narrows the list. Any other reason (selection, blur, clear) means
  // the input shows a label or nothing, which must not be sent as a query.
  const onInputChange = (
    _event: React.SyntheticEvent,
    text: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    setQuery(reason === "input" && text ? text : undefined);
  };

  const valueAdornment =
    value !== null && renderValueAdornment
      ? renderValueAdornment(value)
      : undefined;

  return (
    <Grid size={{ xs: 12, md: 6, xl: 3 }} data-testid={testId}>
      <PaperFooterContext.Provider value={paperFooter}>
        <Autocomplete
          fullWidth
          disabled={disabled}
          value={value}
          renderInput={(inputParams) => (
            <TextField
              {...inputParams}
              label={label}
              InputProps={{
                ...inputParams.InputProps,
                startAdornment: valueAdornment && (
                  <InputAdornment position="start">
                    {valueAdornment}
                  </InputAdornment>
                ),
              }}
              inputProps={{
                ...inputParams.inputProps,
                style: { minWidth: 0 },
              }}
            />
          )}
          renderOption={({ key, ...optionProps }, option) => (
            <Box component="li" key={key} {...optionProps}>
              {renderOptionContent(option)}
            </Box>
          )}
          loading={isLoading && !hasOptions}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onChange={(_event, option) => onChange(option)}
          onInputChange={onInputChange}
          options={options}
          filterOptions={(o) => o}
          slots={{ paper: PaperWithFooter }}
          slotProps={{ listbox: { onScroll: onListboxScroll } }}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          noOptionsText={
            error ? (
              <Alert sx={{ mx: -2, my: -1.8 }} severity="error">
                <AlertTitle>{t("error_unexpected_title")}</AlertTitle>
                {error.message}
              </Alert>
            ) : (
              noResultsText(debouncedQuery)
            )
          }
        />
      </PaperFooterContext.Provider>
    </Grid>
  );
}
